import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import api from '@/utils/axios'; 

import { Link } from 'react-router-dom';
import { Typography, Box, Select, MenuItem, Button } from '@mui/material';
import { useAuth } from '../services/AuthContext'; 
import { getFirestore, doc, getDoc } from "firebase/firestore"; 
import { getAuth } from "firebase/auth"; 
import { countries } from './deliveryCountries'; 

const TopBar = () => {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
    const [deliveryCountry, setDeliveryCountry] = useState(localStorage.getItem('deliveryCountry') || 'DE'); 
    const [userRole, setUserRole] = useState(null); 
    const [loadingRole, setLoadingRole] = useState(true); 

    useEffect(() => {
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage) {
            i18n.changeLanguage(savedLanguage);
            setCurrentLanguage(savedLanguage);
        } else {
            const browserLanguages = navigator.languages || [navigator.language]; 
            const languageMapping = {
                'en-US': 'en-GB',
                'pl-PL': 'pl-PL',
                'fr-FR': 'fr-FR',
                'de-DE': 'de-DE',
            };
    
            let detectedLanguage = 'en-GB'; 
            for (const lang of browserLanguages) {
                if (languageMapping[lang]) {
                    detectedLanguage = languageMapping[lang];
                    break;
                }
            }
            i18n.changeLanguage(detectedLanguage);
            localStorage.setItem('language', detectedLanguage);
            setCurrentLanguage(detectedLanguage);
        }
    }, []);
    
    useEffect(() => {
        if (user) {
            const fetchUserRole = async () => {
                const db = getFirestore();
                const userRef = doc(db, "users", user.uid);
                try {
                    const userDoc = await getDoc(userRef);
                    if (userDoc.exists()) {
                        const role = userDoc.data().role;
                        setUserRole(role);
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                } finally {
                    setLoadingRole(false);
                }
            };
            fetchUserRole();
        }
    }, [user]);

    const handleLanguageChange = async (event) => {
        const selectedLanguage = event.target.value;
        try {
            await i18n.changeLanguage(selectedLanguage);
            localStorage.setItem('language', selectedLanguage);
            setCurrentLanguage(selectedLanguage);
        } catch (error) {
            console.error('Error changing language:', error);
        }
    };

    const handleLogout = () => {
        logout()
            .then(() => {
                window.location.href = '/';
            })
            .catch((error) => {
                console.error('Logout error:', error);
            });
    };

    const getDashboardLink = () => {
        if (userRole) {
            return `/${userRole.toLowerCase()}/dashboard`;
        }
        return null;
    };

    const languageOptions = [ 
        { value: 'en-GB', label: t('English (UK)') },
        { value: 'pl-PL', label: t('Polski (Poland)') },
        { value: 'fr-FR', label: t('Français (France)') },
        { value: 'de-DE', label: t('Deutsch (Germany)') },
        { value: 'de-CH', label: t('Deutsch (Switzerland)') },
        { value: 'fr-CH', label: t('Français (Switzerland)') }
    ];

    return (
        <Box 
            className="top-bar" 
            sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 2,
                flexDirection: { xs: 'column', sm: 'row' }, // Stack on small screens
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                    <img src="/images/notedin.icon.png" alt="notedin" style={{ height: '40px', marginRight: '20px' }} />
                </Link>
                <Select 
                    value={currentLanguage} 
                    onChange={handleLanguageChange} 
                    sx={{ mr: 1, fontSize: { xs: '0.75rem', sm: '1rem' } }} // Adjust font size for small screens
                >
                    {languageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                {user ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 2, color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {t('hello')} {user.displayName || user.email}!
                        </Typography>
                        {loadingRole ? (
                            <Typography variant="body2" sx={{ mr: 2, color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {t('loadingRole')}
                            </Typography>
                        ) : (
                            <Link to={getDashboardLink()} className="top-bar-link" style={{ textDecoration: 'none', color: 'white' }}>
                                <Button size="small">
                                    <Typography variant="body2" sx={{ color: 'white', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                                        {t('Dashboard')}
                                    </Typography>
                                </Button>
                            </Link>
                        )}
                        <Button onClick={handleLogout} style={{ textDecoration: 'none', color: 'white' }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                                {t('Logout')}
                            </Typography>
                        </Button>
                    </Box>
                ) : (
                    <Fragment>
                        <Link to="/signup" className="top-bar-link" style={{ textDecoration: 'none', color: 'white' }}>
                            <Typography variant="body2" sx={{ mr: 2, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                                {t('Register')}
                            </Typography>
                        </Link>
                        <Link to="/signin" className="top-bar-link" style={{ textDecoration: 'none', color: 'white' }}>
                            <Typography variant="body2" sx={{ color: 'white', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                                {t('Login')}
                            </Typography>
                        </Link>
                    </Fragment>
                )}
            </Box>
        </Box>
    );
};

export default TopBar;
